import { FC, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { FormField } from '@/components/FormField';
import { EditableSelect, type SelectOption } from '@/components/ui/editable-select';
import { EditableTextarea, type MarkdownMode } from '@/components/ui/editable-textarea';
import { Button } from '@/components/ui/button';
import { CharacterCount } from '@/components/ui/character-count';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PACKET_VALIDATION, Ratification } from '@promo/shared';

export interface RatificationFormProps {
  initialRatification?: Ratification | null;
  initialOverruleJustification?: string;
  onSubmit: (data: { ratification: Ratification; justification?: string }) => Promise<void> | void;
  isSubmitting?: boolean;
  /** If true, the entire form is non-interactive (fields are disabled, no edit mode). Defaults to false. */
  readOnly?: boolean;
}

export const RatificationForm: FC<RatificationFormProps> = ({
  initialRatification,
  initialOverruleJustification = '',
  onSubmit,
  isSubmitting = false,
  readOnly = false,
}) => {
  const [selectedRatification, setSelectedRatification] = useState<Ratification | undefined>(
    initialRatification ?? undefined
  );
  const [justification, setJustification] = useState(initialOverruleJustification);
  const [isInEditMode, setIsInEditMode] = useState<boolean>(false); // Start in readonly mode
  const [justificationMarkdownMode, setJustificationMarkdownMode] = useState<MarkdownMode>('edit');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [focusTarget, setFocusTarget] = useState<'decision' | 'justification' | null>(null);
  const [ratificationSelectKey, setRatificationSelectKey] = useState(0);

  const decisionRef = useRef<HTMLButtonElement | null>(null);
  const justificationRef = useRef<HTMLTextAreaElement | null>(null);

  const ratificationOptions: SelectOption[] = [
    { value: Ratification.RATIFIED, label: 'Ratified' },
    { value: Ratification.OVERRULED, label: 'Overruled' },
  ];

  // Effect to sync internal state with prop changes (e.g., after save)
  useEffect(() => {
    setSelectedRatification(initialRatification ?? undefined);
    // If the incoming initialRatification is not OVERRULED, clear justification,
    // otherwise, use the initialOverruleJustification prop.
    if (initialRatification !== Ratification.OVERRULED) {
      setJustification('');
    } else {
      setJustification(initialOverruleJustification);
    }
  }, [initialRatification, initialOverruleJustification]);

  useEffect(() => {
    // If readOnly is toggled, exit edit mode
    if (readOnly) {
      setIsInEditMode(false);
    }
  }, [readOnly]);

  useLayoutEffect(() => {
    if (isInEditMode && !readOnly && focusTarget) {
      const timerId = setTimeout(() => {
        if (focusTarget === 'justification' && justificationRef.current) {
          justificationRef.current.focus();
        } else if (focusTarget === 'decision' && decisionRef.current) {
          decisionRef.current.focus();
          decisionRef.current.click(); // To open the select dropdown
        }
      }, 0);
      return () => clearTimeout(timerId);
    }
  }, [isInEditMode, focusTarget, readOnly]);

  const handleEnterEditMode = (target: 'decision' | 'justification') => {
    if (readOnly) return;
    setIsInEditMode(true);
    setFocusTarget(target);
    // Reset markdown mode to edit when entering edit mode
    if (target === 'justification') {
      setJustificationMarkdownMode('edit');
    }
  };

  const handleSave = () => {
    if (readOnly || !selectedRatification) return;
    if (selectedRatification === Ratification.OVERRULED && justification.trim().length === 0) {
      return;
    }
    if (justification.length > PACKET_VALIDATION.MAX_OVERRULE_JUSTIFICATION_LENGTH) return;
    onSubmit({ ratification: selectedRatification, justification });
    setFocusTarget(null);
    setIsInEditMode(false); // Return to readonly mode after save
  };

  const handleActualCancel = () => {
    setSelectedRatification(initialRatification ?? undefined);
    setJustification(initialOverruleJustification);
    setIsInEditMode(false);
    setFocusTarget(null);
    setShowCancelConfirm(false);
    setRatificationSelectKey((prevKey) => prevKey + 1); // Increment key to force re-render
  };

  const handleCancelClick = () => {
    if (readOnly) return;
    const hasChanges =
      selectedRatification !== (initialRatification ?? undefined) ||
      justification !== initialOverruleJustification;
    if (hasChanges && isInEditMode) {
      setShowCancelConfirm(true);
    } else {
      handleActualCancel();
    }
  };

  // Check if ratification is empty and should show callout
  const isRatificationEmpty = !selectedRatification;
  const shouldShowRatificationCallout = isRatificationEmpty && !readOnly;

  return (
    <TooltipProvider>
      <section className="mb-6 space-y-4" onPointerDown={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-semibold mb-4">Ratification</h2>
        <div className="space-y-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <label
                htmlFor="ratification-select"
                className="text-sm font-medium text-gray-500 relative inline-flex items-center cursor-pointer"
              >
                Ratification
                {shouldShowRatificationCallout && (
                  <span className="relative flex w-2 h-2 ml-1">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                    <span className="relative inline-flex rounded-full w-2 h-2 bg-secondary"></span>
                  </span>
                )}
              </label>
            </TooltipTrigger>
            {shouldShowRatificationCallout && (
              <TooltipContent className="bg-background border ml-4" side="bottom" align="start">
                <p>Ratify or overrule the decision for this packet</p>
              </TooltipContent>
            )}
          </Tooltip>
          <div className="relative">
            {!isInEditMode && !readOnly && (
              <div
                className="absolute inset-0 z-10 cursor-pointer"
                aria-label="Edit ratification decision"
                onClick={() => handleEnterEditMode('decision')}
              />
            )}
            <EditableSelect
              ref={decisionRef}
              key={ratificationSelectKey}
              options={ratificationOptions}
              value={selectedRatification}
              onValueChange={(val) => {
                if (readOnly) return;
                setSelectedRatification(val as Ratification);
                setFocusTarget('decision');
              }}
              disabled={!isInEditMode || isSubmitting || readOnly}
              placeholder="Select ratification"
              contentClassName="bg-background text-popover-foreground"
              itemClassName="text-base"
            />
          </div>
        </div>

        {selectedRatification === Ratification.OVERRULED && (
          <FormField label="Justification">
            <div className="relative">
              {!isInEditMode && !readOnly && (
                <div
                  className="absolute inset-0 z-10 cursor-pointer"
                  aria-label="Edit justification"
                  onClick={() => handleEnterEditMode('justification')}
                />
              )}
              <EditableTextarea
                ref={justificationRef}
                value={justification}
                onChange={(e) => {
                  if (readOnly) return;
                  setJustification(e.target.value);
                }}
                markdown={true}
                showModeToggle={isInEditMode && !readOnly}
                markdownMode={justificationMarkdownMode}
                onModeChange={setJustificationMarkdownMode}
                readOnly={!isInEditMode || readOnly}
                disabled={!isInEditMode || isSubmitting || readOnly}
                placeholder="Provide justification for Overrule (required)"
                maxLength={PACKET_VALIDATION.MAX_OVERRULE_JUSTIFICATION_LENGTH}
                rows={4}
                className="min-h-[100px]"
              />
              {isInEditMode && !readOnly && (
                <CharacterCount
                  current={justification.length}
                  max={PACKET_VALIDATION.MAX_OVERRULE_JUSTIFICATION_LENGTH}
                />
              )}
            </div>
          </FormField>
        )}

        {isInEditMode && !readOnly && (
          <div className="flex justify-end gap-2 mt-3">
            <Button
              variant="outline"
              type="button"
              onClick={handleCancelClick}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={
                !selectedRatification ||
                (selectedRatification === Ratification.OVERRULED &&
                  justification.trim().length === 0) ||
                justification.length > PACKET_VALIDATION.MAX_OVERRULE_JUSTIFICATION_LENGTH ||
                isSubmitting
              }
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}

        <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard changes?</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Discard them?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting}>Continue Editing</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleActualCancel}
                disabled={isSubmitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>
    </TooltipProvider>
  );
};
